import type { PostHog } from 'posthog-js'
import { isDevMock } from '../dev/isDevMock'

// Path on qa.replay.io that proxies through to PostHog (netlify.toml, plus the generated
// dist/_redirects in vite.config.ts). Two reasons it exists rather than pointing api_host straight
// at PostHog: the CSP in netlify.toml allows scripts from 'self' only, and the SDK lazily injects
// its own <script> tags (recorder, surveys, toolbar) from the asset host — the same unpinned
// cross-origin script execution that /segment-cdn was introduced to stop (issue #4072). Serving
// them from our origin also keeps ingest off the hostnames tracking blockers filter on.
// www.replay.io proxies the identical path in its next.config.js.
const PROXY_PATH = '/rly'

// PostHog's real app URL. api_host points at the proxy, so without this the SDK builds links back
// into qa.replay.io/rly (toolbar, replay links) instead of the PostHog UI.
const UI_HOST = 'https://us.posthog.com'

// The token comes from Infisical (prod), inlined at build time by `vite build`, and the preview
// workflow pulls that same prod environment — so the token alone cannot gate capture. The shared
// .replay.io cookie that makes cross-subdomain identity work is only readable on the canonical
// host anyway, so anywhere else would report a second, disconnected person.
const PRODUCTION_ORIGIN = 'https://qa.replay.io'

const configuredKey = import.meta.env.VITE_POSTHOG_KEY
const PROJECT_KEY = configuredKey === '' ? '' : configuredKey?.trim()

function captureEnabled(): boolean {
  return Boolean(PROJECT_KEY) && !isDevMock() && window.location.origin === PRODUCTION_ORIGIN
}

// Like the Segment client next door, the SDK renders no user-visible UI, so it must not land in the
// eagerly-loaded entry bundle: importing it dynamically gives it its own deferred chunk. Every
// exported function below stays synchronous and fire-and-forget — nothing in the app awaits
// analytics.
let clientPromise: Promise<PostHog | null> | null = null

function loadPostHog(): Promise<PostHog | null> {
  if (!captureEnabled()) return Promise.resolve(null)
  if (!clientPromise) {
    clientPromise = import('posthog-js')
      .then(({ default: posthog }) => {
        posthog.init(PROJECT_KEY as string, {
          api_host: PROXY_PATH,
          ui_host: UI_HOST,

          // The cookie half of this pair is the part that crosses the subdomain boundary;
          // localStorage is per-origin and www.replay.io cannot read ours.
          persistence: 'localStorage+cookie',
          // Write the cookie on .replay.io rather than qa.replay.io, so someone who read the
          // marketing site and then signed in here stays one person instead of splitting into two
          // anonymous profiles.
          cross_subdomain_cookie: true,
          // Each origin still keeps its own localStorage copy of that identity, and a stale copy
          // otherwise beats the shared cookie — which breaks the hand-off silently, and precisely
          // for the returning visitor we most want to connect. Let the cookie win.
          cookieWinsOnConflict: true,

          // This is a single-page app: route changes are history pushes, so the default load-time
          // pageview would only ever fire for the entry route.
          capture_pageview: 'history_change',

          // FullStory already records sessions here, through the capture pipeline in lib/fullstory,
          // and replays proxied through Netlify bill against our bandwidth quota. Revisit when we
          // decide which recorder we are standardizing on.
          disable_session_recording: true,
        })
        return posthog
      })
      .catch((error: unknown) => {
        // A failed load (offline, adblockers) drops the cached attempt so a later call can retry;
        // the rejection is rethrown for the caller's best-effort handler to absorb.
        clientPromise = null
        throw error
      })
  }
  return clientPromise
}

/** Boot PostHog. Pageviews are captured by the SDK itself from here on (capture_pageview above). */
export function initPostHog(): void {
  void loadPostHog().catch(() => {
    // analytics must never throw into the app
  })
}

export interface PostHogUser {
  id: string
  name: string
  email: string | null
}

/**
 * Attach the signed-in user. This is the call that closes the loop on cross-subdomain tracking:
 * the anonymous marketing-site activity already carried on the shared cookie gets merged into the
 * identified person, so a visit to replay.io and the signup it led to read as one journey.
 */
export function identifyPostHogUser(user: PostHogUser | null): void {
  void loadPostHog()
    .then(client => {
      if (!client || !user) return
      client.identify(user.id, {
        name: user.name,
        ...(user.email ? { email: user.email } : {}),
      })
    })
    .catch(() => {
      // analytics must never throw into the app
    })
}

/**
 * Drop the identified person on sign-out so the next user of a shared browser starts clean. Clears
 * the .replay.io cookie too, which is what keeps the reset from being undone by www.replay.io.
 */
export function resetPostHogUser(): void {
  void loadPostHog()
    .then(client => client?.reset())
    .catch(() => {
      // analytics must never throw into the app
    })
}
